/**
 * SearchResultsBanner.js
 *
 * "Showing search results for …" strip rendered above a search-tab table.
 *
 * Bulk searches paste hundreds of numbers into the search box; rendering that
 * raw string wraps into a wall of text that pushes the table off screen. So a
 * short query renders on one truncated line, and a multi-term query collapses
 * to a preview plus a Show all toggle whose expanded state scrolls inside a
 * capped height.
 */

import React, { useState } from "react";

/** Terms shown before the "+N more" cutoff. */
const TERM_PREVIEW_COUNT = 5;

/** Search boxes accept numbers separated by whitespace, commas, or semicolons. */
export const splitSearchTerms = (query) =>
  (query || "").split(/[\s,;]+/).filter(Boolean);

const SearchResultsBanner = ({ query, suffix = null }) => {
  const [showAllTerms, setShowAllTerms] = useState(false);

  if (!query) return null;

  const terms = splitSearchTerms(query);
  const isBulkSearch = terms.length > TERM_PREVIEW_COUNT;

  return (
    <div className="shrink-0 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
      {isBulkSearch ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            Showing search results for{" "}
            <span className="font-semibold">{terms.length} terms</span>:{" "}
            <span
              className={`font-semibold ${
                showAllTerms ? "block max-h-16 overflow-y-auto break-words" : ""
              }`}
            >
              {showAllTerms
                ? terms.join(", ")
                : `${terms.slice(0, TERM_PREVIEW_COUNT).join(", ")} +${
                    terms.length - TERM_PREVIEW_COUNT
                  } more`}
            </span>
            {suffix}
          </div>
          <button
            onClick={() => setShowAllTerms((p) => !p)}
            className="shrink-0 underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100"
          >
            {showAllTerms ? "Show less" : "Show all"}
          </button>
        </div>
      ) : (
        <div className="truncate">
          Showing search results for:{" "}
          <span className="font-semibold">"{query}"</span>
          {suffix}
        </div>
      )}
    </div>
  );
};

export default SearchResultsBanner;
